import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { apiClient } from '../lib/api';

const POLL_MS = 3 * 60 * 1000;
const CACHE_KEY = 'mig_ticker_v1';

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // private mode or storage quota exceeded — ignore
  }
}

const TickerPricesContext = createContext(null);

export function TickerPricesProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  const cached = readCache();
  const [data, setData] = useState(cached);
  const [loading, setLoading] = useState(cached === null);
  const timerRef = useRef(null);

  const doFetch = useCallback(async () => {
    try {
      const params = uid ? { uid } : {};
      const res = await apiClient.get('/api/market/ticker', { params });
      setData(res.data);
      writeCache(res.data);
    } catch {
      // keep last known data on error
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  useEffect(() => {
    if (!data) return;
    clearTimeout(timerRef.current);
    if (data.session !== 'closed') {
      timerRef.current = setTimeout(doFetch, POLL_MS);
    }
    return () => clearTimeout(timerRef.current);
  }, [data, doFetch]);

  const value = {
    tickers: data?.tickers ?? [],
    quotes: data?.quotes ?? {},
    session: data?.session ?? 'closed',
    sessionLabel: data?.session_label ?? '',
    loading,
  };

  return (
    <TickerPricesContext.Provider value={value}>
      {children}
    </TickerPricesContext.Provider>
  );
}

export function useTickerPrices() {
  const ctx = useContext(TickerPricesContext);
  if (!ctx) throw new Error('useTickerPrices must be used within TickerPricesProvider');
  return ctx;
}
